import {TFunction} from 'i18next';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {translations} from '../../../../i18n/translations';
import {joinAsExpression} from '../../../functions/string';
import {pdfFooterInfo} from '../base/export-pdf-footer-info';
import {pdfHeaderInfo} from '../base/export-pdf-header-info';
import {pdfAddBodyTitle} from '../base/export-pdf-footer-header-utils';
import {PDF_FONT_MAIN_COLOR} from '../base/export-pdf-font';
import {buildExportFileName} from '../base/export-file-name';
import type {PreparedPdfExportFile} from '../base/export-data-types';

export function prepareProfessionalCaregiversListPDF({
  reportData,
  entity,
  t,
}: {
  reportData: any[];
  entity?: EntityInfo;
  t: TFunction;
}): PreparedPdfExportFile {
  const pdf = new jsPDF({orientation: 'landscape', compress: true});
  pdf.setProperties({
    title: `${entity?.shortName ?? entity?.name ?? ''} - ${t(
      translations.professional_caregivers.root.title
    )}`,
  });
  pdf.setTextColor(PDF_FONT_MAIN_COLOR);

  const reportTitle = t(translations.role_permissions['team/professionals']);
  const tableStartY = pdfAddBodyTitle(pdf, {title: reportTitle});

  const table = reportData.map((entry) => {
    const user = entry.userId;
    const name = user.displayName ?? user.name ?? '';
    const age = user.birthday ? moment().diff(user.birthday, 'years').toString() : '';
    const occupation = user.occupationId ?? '';
    const roles = entry.rolesIds?.length ? joinAsExpression(entry.rolesIds) : '';
    const phone = user.phoneNumber ?? '';
    const email = user.email ?? '';
    const status = user.isActive
      ? t(translations.professional_caregivers.root.state_value_true)
      : t(translations.professional_caregivers.root.state_value_false);

    const cell = (cellContent: string) => ({
      content: cellContent,
      // styles: {fillColor: 'white'},
    });

    return [
      cell(name),
      cell(occupation),
      cell(roles),
      cell(phone),
      cell(email),
      cell(age),
      cell(status),
    ];
  });

  const nameStr = t(translations.professional_caregivers.root.name_header);
  const ageStr = t(translations.office.professional_caregiver.seniors.table_view_screen.age);
  const occupationStr = t(translations.user_form.profession_input);
  const rolesStr = t(translations.user_form.roles_ids_input);
  const phoneStr = t(translations.user_form.mobile_phone_number_input);
  const emailStr = t(translations.user_form.email_input);
  const statusStr = t(translations.professional_caregivers.root.state_header);

  autoTable(pdf, {
    body: table,
    head: [[nameStr, occupationStr, rolesStr, phoneStr, emailStr, ageStr, statusStr]],
    startY: tableStartY,
    styles: {fontSize: 9},
    tableWidth: 'auto',
    rowPageBreak: 'avoid',
    columnStyles: {
      0: {cellWidth: 40},
      1: {cellWidth: 50},
      2: {cellWidth: 50},
    },
    willDrawPage: function (data) {
      // ---- Add Header to first page ----
      if (data.pageNumber === 1) {
        pdfHeaderInfo(pdf, undefined, entity, t);

        // Reset custom margins for the rest of the pages
        data.settings.margin.top = 7;
        data.settings.margin.bottom = 15;
      }
    },
    didDrawPage: function (data) {
      pdfFooterInfo(pdf, data.pageNumber.toString());
    },
  });

  const fileName = buildExportFileName([
    entity?.shortName ?? entity?.name,
    t(translations.professional_caregivers.root.title),
  ]);

  return {
    file: pdf,
    fileName,
  };
}
